import { useMemo } from 'react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@easyflow/ui-frontend/components/ui/tooltip';
import UserAvatar from '@/components/common/UserAvatar';
import type { UnifiedEvent } from './types';

export const ADMIN_TIME_BANDS = [
  { id: 'early',     label: 'Mattina presto', short: 'Pres.', startH: 6,  endH: 9  },
  { id: 'morning',   label: 'Mattina',        short: 'Matt.', startH: 9,  endH: 13 },
  { id: 'afternoon', label: 'Pomeriggio',     short: 'Pom.',  startH: 13, endH: 17 },
  { id: 'late',      label: 'Pomeriggio tardi', short: 'P.tardi', startH: 17, endH: 19 },
  { id: 'evening',   label: 'Sera',           short: 'Sera',  startH: 19, endH: 22 },
] as const;

interface CalendarUserLike {
  id: string;
  name?: string;
  email: string;
  role?: string;
  avatar_url?: string | null;
}

interface Props {
  users: CalendarUserLike[];
  events: UnifiedEvent[];
  onCellClick?: (userId: string, bandStartHour: number) => void;
}

function intensityClass(count: number) {
  if (count === 0) return 'bg-gray-50 dark:bg-gray-800/40 text-gray-400';
  if (count === 1) return 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-200';
  if (count <= 3)  return 'bg-purple-300 dark:bg-purple-700/60 text-purple-900 dark:text-white';
  return 'bg-purple-600 text-white';
}

export default function CockpitAdminMatrix({ users, events, onCellClick }: Props) {
  const grid = useMemo(() => {
    const map: Record<string, Record<string, UnifiedEvent[]>> = {};
    for (const u of users) {
      map[u.id] = {};
      for (const b of ADMIN_TIME_BANDS) map[u.id]![b.id] = [];
    }
    for (const e of events) {
      if (e.all_day || !e.assigned_to_id || !map[e.assigned_to_id]) continue;
      const d = new Date(e.start_time_iso);
      const h = d.getHours() + d.getMinutes() / 60;
      const band = ADMIN_TIME_BANDS.find(b => h >= b.startH && h < b.endH);
      if (band) map[e.assigned_to_id]![band.id]!.push(e);
    }
    return map;
  }, [users, events]);

  if (users.length < 2) return null;

  return (
    <TooltipProvider delayDuration={150}>
      <motion.div
        layout
        className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4 shadow-sm"
      >
        <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
          <div>
            <div className="text-sm font-semibold text-gray-900 dark:text-white">Carico per consulente</div>
            <div className="text-[11px] text-gray-500 dark:text-gray-400">Click su una cella per aprire il Planning su quella fascia</div>
          </div>
          <div className="flex items-center gap-2 text-[10px] text-gray-500 dark:text-gray-400">
            <span className="uppercase tracking-wider font-semibold">Intensità</span>
            <div className="flex items-center gap-1">
              <span className="w-4 h-4 rounded bg-gray-50 dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700" title="Nessun evento" />
              <span>0</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="w-4 h-4 rounded bg-purple-100 dark:bg-purple-900/30" title="1 evento" />
              <span>1</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="w-4 h-4 rounded bg-purple-300 dark:bg-purple-700/60" title="2-3 eventi" />
              <span>2–3</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="w-4 h-4 rounded bg-purple-600" title="4 o più eventi" />
              <span>4+</span>
            </div>
          </div>
        </div>
        <div className={cn(
          'overflow-x-auto -mx-4 px-4',
          users.length > 4 && 'max-h-[280px] overflow-y-auto',
        )}>
          <table className="w-full table-fixed border-separate border-spacing-1">
            <colgroup>
              <col className="w-[110px] sm:w-[140px]" />
              {ADMIN_TIME_BANDS.map(b => (
                <col key={b.id} />
              ))}
            </colgroup>
            <thead>
              <tr>
                <th className="text-left text-[10px] uppercase tracking-wider text-gray-400 font-semibold pl-1">Cons.</th>
                {ADMIN_TIME_BANDS.map(b => (
                  <th key={b.id} className="text-center text-[10px] uppercase tracking-wider text-gray-400 font-semibold px-0.5" title={b.label}>
                    <span className="block truncate">{b.short}</span>
                    <div className="text-[9px] font-normal text-gray-300 normal-case tracking-normal">
                      {String(b.startH).padStart(2,'0')}–{String(b.endH).padStart(2,'0')}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id}>
                  <td className="py-0.5 pr-2 sticky left-0 bg-white dark:bg-gray-900 z-10">
                    <div className="flex items-center gap-2 min-w-[160px]">
                      <UserAvatar id={u.id} name={u.name} email={u.email} avatarUrl={u.avatar_url || undefined} size="sm" />
                      <span className="text-xs font-medium text-gray-700 dark:text-gray-200 truncate">
                        {u.name || u.email}
                      </span>
                    </div>
                  </td>
                  {ADMIN_TIME_BANDS.map(b => {
                    const cell = grid[u.id]?.[b.id] || [];
                    const tip = cell.length === 0
                      ? `${u.name || u.email} · ${b.label}: nessun evento`
                      : cell.map(e => `• ${e.title}`).join('\n');
                    return (
                      <td key={b.id} className="text-center">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              type="button"
                              onClick={() => onCellClick?.(u.id, b.startH)}
                              aria-label={`${u.name || u.email}, ${b.label}: ${cell.length} ${cell.length === 1 ? 'evento' : 'eventi'}`}
                              className={cn(
                                'w-full h-9 rounded-md flex items-center justify-center text-sm font-semibold transition-all hover:scale-[1.04] hover:shadow-md',
                                'focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-400 focus-visible:ring-offset-1 dark:focus-visible:ring-offset-gray-900',
                                intensityClass(cell.length),
                              )}
                            >
                              <span aria-hidden="true">{cell.length || '·'}</span>
                            </button>
                          </TooltipTrigger>
                          <TooltipContent side="top" className="max-w-[280px] whitespace-pre-line">
                            {tip}
                          </TooltipContent>
                        </Tooltip>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </motion.div>
    </TooltipProvider>
  );
}
